import { SpotStatus, type RoomLayoutSpot } from '../components/RoomLayoutMap/RoomLayoutMap.types';
import {
  getCanonicalSpotName,
  normalizeSpot,
  type NormalizedSpot
} from '../components/RoomLayoutMap/spotKey';
import type {
  BookSpotReserveMeta,
  BookSpotReserveParams,
  InteractiveSpotBookingParamsLike,
  LastBookSpotContext,
  LastChangeSpotContext,
  MissingSpotFailureReason
} from './types';
import {
  buildBookSpotReserveMeta,
  isFirstSpotAssignment,
  resolvePreviousSpotIdForChangeSpot
} from './spotBookingLogic';

/** Value for POST body `spot_id` (label preferred when API sends both UUID and name). */
export function getSpotPayloadForBookSpot(normalized: NormalizedSpot): string | null {
  return normalized.displayLabel ?? normalized.apiSpotId;
}

export type ExecuteBookSpotActions = {
  setQuickBookPendingClose: () => void;
  setLastBookSpotContext: (ctx: LastBookSpotContext) => void;
  trackSpotBookingCtaClicked: () => void;
  trackSpotBookingRequestSent: (meta: LastBookSpotContext) => void;
  trackInteractiveSpotChangeRequestSent: (meta: LastChangeSpotContext) => void;
  setLastChangeSpotContext: (ctx: LastChangeSpotContext) => void;
  onMissingSpotId?: (reason: MissingSpotFailureReason) => void;
  bookSpot: (
    body: { bookingId: string; spotPayload: string },
    meta: BookSpotReserveMeta<BookSpotReserveParams>
  ) => void;
  bookingId: string;
  eventId: string;
  isChangeSpotFlow: boolean;
  previousSpotId: string;
};

function omitIsChangeSpotFlow(
  params: InteractiveSpotBookingParamsLike
): Omit<InteractiveSpotBookingParamsLike, 'isChangeSpotFlow'> {
  const { isChangeSpotFlow: _isChangeSpotFlow, ...rest } = params;
  void _isChangeSpotFlow;
  return rest;
}

function buildBookParamsForReserve(
  params: InteractiveSpotBookingParamsLike,
  isChangeSpotFlow: boolean,
  resolvedSpotId: string,
  displayLabel: string | null | undefined
): BookSpotReserveParams {
  return {
    ...omitIsChangeSpotFlow(params),
    ...(isChangeSpotFlow ? { isChangeSpotFlow: true as const } : {}),
    bookSpotPayload: resolvedSpotId,
    ...(displayLabel != null ? { spotDisplayLabel: displayLabel } : {})
  };
}

export function executeBookSpot<TParams extends InteractiveSpotBookingParamsLike>(
  selectedSpot: RoomLayoutSpot | null,
  isQuickBook: boolean,
  shouldDeferModalClose: boolean,
  params: TParams,
  actions: ExecuteBookSpotActions
): void {
  if (!selectedSpot) return;
  actions.trackSpotBookingCtaClicked();
  const resolvedPreviousSpotId = actions.isChangeSpotFlow
    ? resolvePreviousSpotIdForChangeSpot(params, actions.previousSpotId)
    : '';
  const normalized = normalizeSpot(selectedSpot);
  const resolvedSpotId = getSpotPayloadForBookSpot(normalized);
  if (resolvedSpotId === null) {
    actions.onMissingSpotId?.('missing_spot_id');
    return;
  }
  if (actions.isChangeSpotFlow && resolvedPreviousSpotId === '' && !isFirstSpotAssignment(params)) {
    actions.onMissingSpotId?.('missing_previous_spot_id');
    return;
  }
  const context: LastBookSpotContext = {
    timeSlotId: actions.eventId,
    spotTypePrefix: selectedSpot.spot_type_prefix ?? '',
    spotNumber: resolvedSpotId
  };
  actions.setLastBookSpotContext(context);
  if (actions.isChangeSpotFlow && resolvedPreviousSpotId !== '') {
    const changeContext: LastChangeSpotContext = {
      timeSlotId: actions.eventId,
      previousSpotId: resolvedPreviousSpotId,
      spotId: resolvedSpotId
    };
    actions.setLastChangeSpotContext(changeContext);
    actions.trackInteractiveSpotChangeRequestSent(changeContext);
  } else {
    actions.trackSpotBookingRequestSent(context);
  }
  if (shouldDeferModalClose) actions.setQuickBookPendingClose();
  const bookParams = buildBookParamsForReserve(
    params,
    actions.isChangeSpotFlow,
    resolvedSpotId,
    normalized.displayLabel
  );
  actions.bookSpot(
    { bookingId: actions.bookingId, spotPayload: resolvedSpotId },
    buildBookSpotReserveMeta(
      isQuickBook,
      params,
      bookParams,
      resolvedPreviousSpotId,
      actions.isChangeSpotFlow
    )
  );
}

export type PerformInteractiveBookSpotArgs<TParams extends InteractiveSpotBookingParamsLike> = {
  bookingId: string;
  selectedSpot: RoomLayoutSpot | null;
  isQuickBook: boolean;
  shouldDeferModalClose: boolean;
  params: TParams;
  dismissBookSpotError: () => void;
  lastAttemptedSpotGridKeyRef: { current: string | null };
  setQuickBookPendingClose: () => void;
  setLastBookSpotContext: (ctx: LastBookSpotContext) => void;
  trackSpotBookingCtaClicked: () => void;
  trackSpotBookingRequestSent: (meta: LastBookSpotContext) => void;
  trackInteractiveSpotChangeRequestSent: (meta: LastChangeSpotContext) => void;
  setLastChangeSpotContext: (ctx: LastChangeSpotContext) => void;
  onMissingSpotId: (reason: MissingSpotFailureReason) => void;
  bookSpot: ExecuteBookSpotActions['bookSpot'];
  eventId: string;
  isChangeSpotFlow: boolean;
  previousSpotId: string;
};

export function performInteractiveBookSpot<TParams extends InteractiveSpotBookingParamsLike>(
  args: PerformInteractiveBookSpotArgs<TParams>,
  skipInFlightRef: { current: boolean }
): void {
  if (skipInFlightRef.current) return;

  const {
    bookingId,
    selectedSpot,
    isQuickBook,
    shouldDeferModalClose,
    params,
    dismissBookSpotError,
    lastAttemptedSpotGridKeyRef,
    setQuickBookPendingClose,
    setLastBookSpotContext,
    trackSpotBookingCtaClicked,
    trackSpotBookingRequestSent,
    trackInteractiveSpotChangeRequestSent,
    onMissingSpotId,
    bookSpot,
    eventId,
    isChangeSpotFlow,
    previousSpotId
  } = args;

  if (!bookingId) return;
  if (!selectedSpot) return;

  dismissBookSpotError();
  const normalizedSelectedSpot = normalizeSpot(selectedSpot);
  lastAttemptedSpotGridKeyRef.current = getCanonicalSpotName(normalizedSelectedSpot);
  executeBookSpot(selectedSpot, isQuickBook, shouldDeferModalClose, params, {
    setQuickBookPendingClose,
    setLastBookSpotContext,
    trackSpotBookingCtaClicked,
    trackSpotBookingRequestSent,
    trackInteractiveSpotChangeRequestSent,
    setLastChangeSpotContext: args.setLastChangeSpotContext,
    onMissingSpotId,
    bookSpot,
    bookingId,
    eventId,
    isChangeSpotFlow,
    previousSpotId
  });
}

export function handleInteractiveSpotSelect(
  spot: RoomLayoutSpot,
  eventId: string,
  trackSpotTapped: (meta: {
    timeSlotId: string;
    spotTypePrefix: string;
    spotNumber: string;
    isBookable: boolean;
  }) => void,
  onSelectSpot: (spot: RoomLayoutSpot) => void,
  skipInFlightRef: { current: boolean }
): void {
  if (skipInFlightRef.current) return;

  const normalized = normalizeSpot(spot);
  const spotNumber = getSpotPayloadForBookSpot(normalized) ?? '';
  trackSpotTapped({
    timeSlotId: eventId,
    spotTypePrefix: spot.spot_type_prefix ?? '',
    spotNumber,
    isBookable: spot.status === SpotStatus.BOOKABLE
  });
  onSelectSpot(spot);
}
